import React, { useMemo } from 'react';
import { Stack, Typography, Box, Button, Alert, Chip, Checkbox, FormControlLabel } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Papa from 'papaparse';
import { toast } from 'react-toastify';

import { DenseTable, DenseColumn } from '../../../../components/groups-teams/shared/DenseTable';
import { GT_COLORS } from '../../../../constants/designTokens.constants';
import { BulkDeletePreviewResponse, MatchedRow, UnmatchedRow } from '../../../../types/bulkActions.types';
import { formatTimeEstimate } from './timeEstimate';

interface PreviewStepProps {
    previewData: BulkDeletePreviewResponse;
    acknowledgedMismatches: boolean;
    onAcknowledgedMismatchesChange: (value: boolean) => void;
    onContinue: () => void;
    onBack: () => void;
}

interface PreviewTableRow {
    rowNumber: number;
    status: 'matched' | 'mismatch' | 'unmatched';
    identifierValue: string;
    name: string;
    details: string;
    _key: string;
}

const statusChipProps: Record<string, { label: string; bg: string; color: string }> = {
    matched: { label: 'Matched', bg: GT_COLORS.greenLight, color: GT_COLORS.green },
    mismatch: { label: 'Mismatch', bg: GT_COLORS.amberLight, color: GT_COLORS.amber },
    unmatched: { label: 'Unmatched', bg: GT_COLORS.badgeGrayBg, color: GT_COLORS.badgeGrayColor },
};

export const PreviewStep: React.FC<PreviewStepProps> = ({
    previewData,
    acknowledgedMismatches,
    onAcknowledgedMismatchesChange,
    onContinue,
    onBack,
}) => {
    const { matched, unmatched, summary } = previewData;

    // Merge matched + unmatched into a single table array
    const tableRows = useMemo<PreviewTableRow[]>(() => {
        const rows: PreviewTableRow[] = [];

        for (const row of matched) {
            const hasMismatch = row.mismatchedFields.length > 0;
            const details = hasMismatch
                ? row.mismatchedFields.map(m => `${m.field}: "${m.providedValue}" vs "${m.userValue}"`).join('; ')
                : '';

            rows.push({
                rowNumber: row.rowNumber,
                status: hasMismatch ? 'mismatch' : 'matched',
                identifierValue: row.providedFields[previewData.identifierField] || '',
                name: [row.userSnapshot.firstName, row.userSnapshot.lastName].filter(Boolean).join(' ') || '-',
                details,
                _key: `matched-${row.rowNumber}`,
            });
        }

        for (const row of unmatched) {
            rows.push({
                rowNumber: row.rowNumber,
                status: 'unmatched',
                identifierValue: row.identifierValue,
                name: [row.providedFields.first_name, row.providedFields.last_name].filter(Boolean).join(' ')
                    || row.providedFields.name || '-',
                details: row.reason,
                _key: `unmatched-${row.rowNumber}`,
            });
        }

        return rows.sort((a, b) => a.rowNumber - b.rowNumber);
    }, [matched, unmatched, previewData.identifierField]);

    // Detect if only identifier column was provided (no verification fields)
    const onlyIdentifierProvided = matched.length > 0 &&
        matched.every(r => Object.keys(r.providedFields).length <= 1);

    const totalMatchedCount = summary.matchedCleanCount + summary.matchedWithMismatchCount;
    const hasMismatches = summary.matchedWithMismatchCount > 0;
    const canContinue = totalMatchedCount > 0 && (!hasMismatches || acknowledgedMismatches);

    const columns: DenseColumn<PreviewTableRow>[] = [
        {
            id: 'rowNumber',
            label: 'Row',
            width: 60,
            sortable: true,
            render: (row) => <Typography fontSize={13}>{row.rowNumber}</Typography>,
            getSortValue: (row) => row.rowNumber,
        },
        {
            id: 'status',
            label: 'Status',
            width: 120,
            render: (row) => {
                const chip = statusChipProps[row.status];
                return (
                    <Chip
                        label={chip.label}
                        size="small"
                        sx={{
                            backgroundColor: chip.bg,
                            color: chip.color,
                            fontWeight: 500,
                            fontSize: 12,
                            height: 24,
                        }}
                    />
                );
            },
        },
        {
            id: 'identifier',
            label: 'Identifier',
            width: 200,
            sortable: true,
            render: (row) => <Typography fontSize={13} noWrap>{row.identifierValue}</Typography>,
            getSortValue: (row) => row.identifierValue,
        },
        {
            id: 'name',
            label: 'Name',
            width: 180,
            sortable: true,
            render: (row) => <Typography fontSize={13}>{row.name}</Typography>,
            getSortValue: (row) => row.name,
        },
        {
            id: 'details',
            label: 'Details',
            render: (row) => (
                <Typography fontSize={12} color={row.status === 'mismatch' ? GT_COLORS.amber : GT_COLORS.textMuted}>
                    {row.details || '-'}
                </Typography>
            ),
        },
    ];

    const handleDownloadPreview = () => {
        try {
            const csvRows = tableRows.map(row => ({
                'Row Number': row.rowNumber,
                'Status': row.status,
                'Identifier': row.identifierValue,
                'Name': row.name,
                'Details': row.details,
            }));

            const csvContent = Papa.unparse(csvRows);
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'bulk-delete-preview.csv';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error('Failed to generate preview CSV. Please try again.');
        }
    };

    return (
        <Stack spacing={3}>
            {/* Summary stats */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {[
                    { label: 'Total Rows', value: summary.totalRows, bg: GT_COLORS.headerBg, color: GT_COLORS.textPrimary },
                    { label: 'Matched (Clean)', value: summary.matchedCleanCount, bg: GT_COLORS.greenBg, color: GT_COLORS.green },
                    { label: 'Matched (Mismatches)', value: summary.matchedWithMismatchCount, bg: GT_COLORS.amberBg, color: GT_COLORS.amber },
                    { label: 'Unmatched', value: summary.unmatchedCount, bg: GT_COLORS.badgeGrayBg, color: GT_COLORS.badgeGrayColor },
                ].map(stat => (
                    <Box
                        key={stat.label}
                        sx={{
                            flex: '1 1 140px',
                            p: 2,
                            borderRadius: '8px',
                            backgroundColor: stat.bg,
                            textAlign: 'center',
                        }}
                    >
                        <Typography fontSize={24} fontWeight={700} color={stat.color}>
                            {stat.value}
                        </Typography>
                        <Typography fontSize={12} color={GT_COLORS.textSecondary}>
                            {stat.label}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Warning: only identifier column */}
            {onlyIdentifierProvided && (
                <Alert severity="warning" sx={{ borderRadius: '8px' }}>
                    You haven't included any additional fields to verify these are the correct users.
                    We strongly recommend re-uploading with at least a name column.
                </Alert>
            )}

            {/* Preview table */}
            <Box sx={{ border: `1px solid ${GT_COLORS.border}`, borderRadius: '10px', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderBottom: `1px solid ${GT_COLORS.borderLight}` }}>
                    <Typography fontSize={14} fontWeight={600} color={GT_COLORS.textPrimary}>
                        Preview ({tableRows.length} rows)
                    </Typography>
                    <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadPreview}
                        sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                        Download Preview CSV
                    </Button>
                </Box>
                <DenseTable<PreviewTableRow>
                    columns={columns}
                    rows={tableRows}
                    getRowKey={(row) => row._key}
                    pageSize={100}
                    defaultSortColumn="rowNumber"
                    defaultSortDir="asc"
                    emptyMessage="No rows to display"
                    rowClassName={(row) => {
                        if (row.status === 'mismatch') return 'bulk-delete-mismatch-row';
                        if (row.status === 'unmatched') return 'bulk-delete-unmatched-row';
                        return undefined;
                    }}
                />
                {/* Inline styles for row highlighting */}
                <style>{`
                    .bulk-delete-mismatch-row { background-color: ${GT_COLORS.amberBg} !important; }
                    .bulk-delete-unmatched-row { background-color: ${GT_COLORS.redBg} !important; }
                `}</style>
            </Box>

            {/* Mismatch acknowledgment */}
            {hasMismatches && (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={acknowledgedMismatches}
                            onChange={(e) => onAcknowledgedMismatchesChange(e.target.checked)}
                        />
                    }
                    label={
                        <Typography fontSize={14} color={GT_COLORS.textPrimary}>
                            I understand that {summary.matchedWithMismatchCount} row{summary.matchedWithMismatchCount !== 1 ? 's have' : ' has'} field
                            mismatches and want to proceed anyway.
                        </Typography>
                    }
                />
            )}

            {/* Action buttons */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                >
                    Back to Upload
                </Button>
                <Stack direction="row" alignItems="center" spacing={2}>
                    {formatTimeEstimate(totalMatchedCount) && (
                        <Typography fontSize={13} color={GT_COLORS.textMuted}>
                            Estimated time: {formatTimeEstimate(totalMatchedCount)}
                        </Typography>
                    )}
                    <Button
                        variant="contained"
                        color="error"
                        disabled={!canContinue}
                        onClick={onContinue}
                        sx={{ textTransform: 'none', borderRadius: '8px' }}
                    >
                        Continue ({totalMatchedCount} user{totalMatchedCount !== 1 ? 's' : ''} to delete)
                    </Button>
                </Stack>
            </Stack>
        </Stack>
    );
};
